<script>
import DialogBase from '@/components/dialogs/DialogBase.vue'
import { download, getDatedFilename, getErrorMessage, RESOLVER_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import { defaultVTooltipProps } from '@/utils/defaultProps'

export default {
  mixins: [DialogBase],

  props: {
    sections: {
      type: Array,
      required: true,
    },
    hierarchy: {
      type: Array,
      required: true,
    },
    report: {
      type: Array,
      required: true,
    },
  },

  data: () => ({
    localSections: [],
    downloading: false,
    search: null,
    defaultVTooltipProps,
  }),

  computed: {
    filtersChanged() {
      return !!this.localSections.length
    },
  },

  watch: {
    localSections: {
      handler: function (val) {
        this.$emit('update:sections', val)
      },
      deep: true,
    },
  },

  methods: {
    onReset() {
      this.localSections = []
    },
    onExportSection(format) {
      this.downloading = true
      this.$axios
        .post(
          `${RESOLVER_URL}/export-section`,
          {
            chemicals: this.report.map((r) => r.chemical),
            sections: this.localSections,
            outputFormat: format,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          this.downloading = false
          let filename = getDatedFilename('safety', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          this.downloading = false
          showErrorToast(error, { title: 'Error during export' })
        })
    },
  },

  mounted() {
    Object.assign(this.localSections, this.sections)
  },
}
</script>

<template>
  <v-dialog
    v-model="dialog"
    :max-width="maxWidth"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        v-if="buttonTitle"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        :aria-label="title"
        :title="title"
        @click="onButtonActivate"
        class="border flat-button"
        id="export-filter-button"
      >
        <template v-if="buttonTitle.startsWith('mdi-')">
          <v-badge
            v-if="filtersChanged"
            icon="mdi-checkbox-multiple-marked-circle-outline"
            color="amber lighten-2"
          >
            <v-icon>
              {{ buttonTitle }}
            </v-icon>
          </v-badge>
          <v-icon v-else>
            {{ buttonTitle }}
          </v-icon>
        </template>
        <template v-else>
          {{ buttonTitle }}
        </template>
      </v-btn>
      <v-tooltip
        top
        v-bind="defaultVTooltipProps"
        activator="#export-filter-button"
      >
        {{ title }}
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>Export Sections</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          hide-details
          label="Search sections..."
          single-line
          :title="`Search sections`"
          id="Export-filters-export-section-search-input"
        />
        <v-tooltip
          top
          v-bind="defaultVTooltipProps"
          activator="#Export-filters-export-section-search-input"
        >
          Search sections to export
        </v-tooltip>

        <v-row dense>
          <v-col>
            <v-treeview
              v-model="localSections"
              selectable
              :items="hierarchy"
              dense
              :search="search"
              open-all
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-b-tooltip
          :disabled="!report || downloading || !localSections.length"
          title="Export Sections to SDF"
          @click="onExportSection('SDF')"
          aria-label="Export Sections to SDF"
        >
          <v-icon>mdi-file-download-outline</v-icon>
        </v-btn>
        <v-btn
          v-b-tooltip
          :disabled="!report || downloading || !localSections.length"
          title="Export Sections to Excel"
          @click="onExportSection('XLSX')"
          aria-label="Export Sections to Excel"
        >
          <v-icon>mdi-file-excel-outline</v-icon>
        </v-btn>
        <v-btn
          v-b-tooltip
          :disabled="!report || downloading || !localSections.length"
          title="Export Sections to Word"
          @click="onExportSection('DOCX')"
          aria-label="Export Sections to Word"
        >
          <v-icon>mdi-file-word-outline</v-icon>
        </v-btn>
        <v-btn
          v-b-tooltip
          :disabled="!report || downloading || !localSections.length"
          title="Export Sections to PDF"
          @click="onExportSection('PDF')"
          aria-label="Export Sections to PDF"
        >
          <v-icon>mdi-file-pdf-box</v-icon>
        </v-btn>
        <v-spacer />
        <span v-if="downloading">Preparing download...</span>
        <v-spacer />
        <v-btn
          @click="onReset"
          aria-label="Reset"
        >
          Reset
        </v-btn>
        <v-btn
          @click="dialog = false"
          aria-label="Close"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
